import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { ExternalProviderRepository } from "@shared-kernel/application/ports/external-provider/external-provider-repository";
import { BackendExternalProviderRepository } from "@shared-kernel/secondary/external-provider/backend-external-provider-repository";

/**
 * Unlike other repositories instantiated in src/index.tsx with tokenStorageGateway,
 * this repository uses a URL-based token (x-external-provider-token header) for authentication.
 * It's instantiated per-session via React Context when the external provider portal loads.
 */
const ExternalProviderRepositoryContext = createContext<ExternalProviderRepository | null>(null);

interface ExternalProviderRepositoryProviderProps {
  accessToken: string;
  children: ReactNode;
}

export const ExternalProviderRepositoryProvider = ({ accessToken, children }: ExternalProviderRepositoryProviderProps) => {
  const repository = useMemo(() => new BackendExternalProviderRepository(accessToken), [accessToken]);

  return <ExternalProviderRepositoryContext.Provider value={repository}>{children}</ExternalProviderRepositoryContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useExternalProviderRepository = (): ExternalProviderRepository => {
  const repository = useContext(ExternalProviderRepositoryContext);
  if (!repository) {
    throw new Error("useExternalProviderRepository must be used within an ExternalProviderRepositoryProvider");
  }
  return repository;
};
